import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadCounts, useSocket, incrementRoomUnread } from '../../hooks/useSocket';
import { 
  LayoutDashboard, 
  Calendar,
  CalendarRange,
  Users, 
  Building2, 
  BarChart3, 
  Download,
  MessageCircle,
  BookOpen,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  Zap,
  User,
  Tag,
  // New icons for Events module
  CalendarDays,
  FileText,
  Store,
  Sparkles,
  FolderKanban
} from 'lucide-react';
import { useState, useEffect } from 'react';

function NavItem({ to, icon: Icon, children, onClick, badge }) {
  return (
    <NavLink 
      to={to} 
      className={({ isActive }) => 
        isActive ? 'nav-link-active' : 'nav-link'
      }
      onClick={onClick}
    >
      <div className="relative">
        <Icon className="w-5 h-5" />
        {badge > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span>{children}</span>
    </NavLink>
  );
}

function NavSection({ title }) {
  return (
    <div className="pt-4 pb-1 px-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</p>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout, isAdmin, isAccountant, isOwner } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { total: unreadCount } = useUnreadCounts();
  const { onRoomUpdated, onRoomCreated } = useSocket();

  // Listen for room updates to increment unread and play sound
  useEffect(() => {
    const unsubscribe = onRoomUpdated(({ roomId }) => {
      // Only increment if not currently on chat page viewing this room
      const isOnChatPage = window.location.pathname.includes('/chat');
      if (!isOnChatPage) {
        incrementRoomUnread(roomId);
      }
    });
    return unsubscribe;
  }, [onRoomUpdated]);

  // Listen for new rooms created (e.g., contractor initiating chat)
  useEffect(() => {
    const unsubscribe = onRoomCreated(({ room }) => {
      // New room created - increment unread to notify user
      const isOnChatPage = window.location.pathname.includes('/chat');
      if (!isOnChatPage && room?.id) {
        incrementRoomUnread(room.id);
      }
    });
    return unsubscribe;
  }, [onRoomCreated]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation organized by section
  const schedulerNav = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { to: '/admin/scheduler', icon: CalendarRange, label: 'Scheduler', show: isAdmin },
    { to: '/admin/schedule', icon: Calendar, label: 'Schedule', show: isAdmin },
    { to: '/admin/templates', icon: Zap, label: 'Templates', show: isAdmin },
    { to: '/admin/roles', icon: Tag, label: 'Manage Roles', show: isAdmin },
  ].filter(item => item.show);

  const eventsNav = [
    { to: '/admin/events', icon: CalendarDays, label: 'All Events', show: isAdmin },
    { to: '/admin/events/builder', icon: FileText, label: 'Event Builder', show: isAdmin },
  ].filter(item => item.show);

  const vendorNav = [
    { to: '/admin/vendors', icon: Store, label: 'All Vendors', show: isAdmin },
    { to: '/admin/vendors/discovery', icon: Sparkles, label: 'AI Discovery', show: isAdmin },
    { to: '/admin/event-categories', icon: FolderKanban, label: 'Manage Categories', show: isAdmin },
  ].filter(item => item.show);

  const peopleNav = [
    { to: '/admin/clients', icon: Building2, label: 'Clients', show: isAdmin },
    { to: '/admin/contractors', icon: Users, label: 'Contractors', show: isAdmin },
    { to: '/admin/users', icon: ShieldCheck, label: 'Admin Users', show: isOwner },
    { to: '/admin/directory', icon: BookOpen, label: 'Directory', show: isAdmin },
  ].filter(item => item.show);

  const toolsNav = [
    { to: '/admin/chat', icon: MessageCircle, label: 'Chat', show: true, badge: unreadCount },
    { to: '/admin/reports', icon: BarChart3, label: 'Reports', show: isAdmin },
    { to: '/admin/exports', icon: Download, label: 'Exports', show: isAccountant },
    { to: '/admin/profile', icon: User, label: 'Profile', show: true },
  ].filter(item => item.show);

  // Flat navigation for mobile (all items)
  const allNavigation = [
    ...schedulerNav,
    ...eventsNav,
    ...vendorNav,
    ...peopleNav,
    ...toolsNav,
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col h-full min-h-0 bg-white border-r border-gray-200">
          {/* Logo */}
          <div className="flex-shrink-0 flex flex-col items-center justify-center py-4 px-6 border-b border-gray-200">
            <img src="/logo.png" alt="HUBTractor" className="h-20" />
            <span className="text-xs text-gray-400 mt-1">by Shaun Flannery</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 px-4 py-4 space-y-1 overflow-y-auto">
            {/* Scheduler Section */}
            <NavSection title="Scheduler" />
            {schedulerNav.map((item) => (
              <NavItem key={item.to} to={item.to} icon={item.icon} badge={item.badge}>
                {item.label}
              </NavItem>
            ))}

            {/* Events Section */}
            {eventsNav.length > 0 && (
              <>
                <NavSection title="Events" />
                {eventsNav.map((item) => (
                  <NavItem key={item.to} to={item.to} icon={item.icon} badge={item.badge}>
                    {item.label}
                  </NavItem>
                ))}
              </>
            )}

            {/* Vendor Hub Section */}
            {vendorNav.length > 0 && (
              <>
                <NavSection title="Vendor Hub" />
                {vendorNav.map((item) => (
                  <NavItem key={item.to} to={item.to} icon={item.icon} badge={item.badge}>
                    {item.label}
                  </NavItem>
                ))}
              </>
            )}

            {/* People Section */}
            <NavSection title="People" />
            {peopleNav.map((item) => (
              <NavItem key={item.to} to={item.to} icon={item.icon} badge={item.badge}>
                {item.label}
              </NavItem>
            ))}

            {/* Tools Section */}
            <NavSection title="Tools" />
            {toolsNav.map((item) => (
              <NavItem key={item.to} to={item.to} icon={item.icon} badge={item.badge}>
                {item.label}
              </NavItem>
            ))}
          </nav>

          {/* User section */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.role}</p>
              </div>
              <button 
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile header with safe area padding for iOS */}
      <div className="lg:hidden">
        <div 
          className="flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <img src="/logo.png" alt="HUBTractor" className="h-9" />
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="relative p-2 text-gray-600 hover:text-gray-900"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            {!mobileMenuOpen && unreadCount > 0 && (
              <span className="absolute top-1 right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div 
            className="absolute inset-x-0 z-50 bg-white border-b border-gray-200 shadow-lg animate-fade-in max-h-[80vh] overflow-y-auto"
            style={{ top: 'calc(4rem + env(safe-area-inset-top, 0px))' }}
          >
            <nav className="px-4 py-2 space-y-1">
              {/* Mobile: Scheduler */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2 pb-1 px-3">Scheduler</p>
              {schedulerNav.map((item) => (
                <NavItem 
                  key={item.to} 
                  to={item.to} 
                  icon={item.icon}
                  badge={item.badge}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </NavItem>
              ))}

              {/* Mobile: Events */}
              {eventsNav.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4 pb-1 px-3">Events</p>
                  {eventsNav.map((item) => (
                    <NavItem 
                      key={item.to} 
                      to={item.to} 
                      icon={item.icon}
                      badge={item.badge}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </NavItem>
                  ))}
                </>
              )}

              {/* Mobile: Vendor Hub */}
              {vendorNav.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4 pb-1 px-3">Vendor Hub</p>
                  {vendorNav.map((item) => (
                    <NavItem 
                      key={item.to} 
                      to={item.to} 
                      icon={item.icon}
                      badge={item.badge}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </NavItem>
                  ))}
                </>
              )}

              {/* Mobile: People */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4 pb-1 px-3">People</p>
              {peopleNav.map((item) => (
                <NavItem 
                  key={item.to} 
                  to={item.to} 
                  icon={item.icon}
                  badge={item.badge}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </NavItem>
              ))}

              {/* Mobile: Tools */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4 pb-1 px-3">Tools</p>
              {toolsNav.map((item) => (
                <NavItem 
                  key={item.to} 
                  to={item.to} 
                  icon={item.icon}
                  badge={item.badge}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </NavItem>
              ))}

              <button 
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg mt-4"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Main content */}
      <main className="lg:pl-64">
        <div className="py-6 px-4 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
